// Dashboard-only: one cheap GET every 5 min for this org/user while the tab is visible.
import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { get } from "../api/client";
import { DASHBOARD_BASE } from "../../../config/featureFlags";

const INTERVAL_MS = 5 * 60 * 1000;

export function useOrgReminderPoll(enabled) {
  const navigate = useNavigate();
  const lastAt = useRef(0);
  const seen = useRef(new Set());
  const orgOff = useRef(false);

  const tick = useCallback(async (force) => {
    if (!enabled || orgOff.current) return;
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    const now = Date.now();
    if (!force && lastAt.current && now - lastAt.current < INTERVAL_MS) return;
    lastAt.current = now;
    try {
      const data = await get("/reminders/due");
      if (data && data.enabled === false) { orgOff.current = true; return; }
      const items = (data && data.items) || [];
      if (items.length) {
        try { window.dispatchEvent(new Event('tg-notifications')); } catch (_e) { /* ignore */ }
      }
      items.forEach((it) => {
        if (!it?.id || seen.current.has(it.id)) return;
        seen.current.add(it.id);
        const label = it.text || `Reminder · ${it.title || "Task"}`;
        toast((t) => (
          <button
            type="button"
            onClick={() => {
              toast.dismiss(t.id);
              if (it.taskId) navigate(`${DASHBOARD_BASE}/tasks/t/${it.taskId}`);
            }}
            style={{ background: "none", border: 0, padding: 0, textAlign: "left", cursor: it.taskId ? "pointer" : "default", font: "inherit", color: "inherit" }}
          >
            {label}
          </button>
        ), { duration: 8000 });
      });
    } catch (e) {
      if (e && e.status === 404) return;
    }
  }, [enabled, navigate]);

  useEffect(() => {
    if (!enabled) return undefined;
    tick(true);
    const id = setInterval(() => { tick(false); }, INTERVAL_MS);
    const onVis = () => { if (document.visibilityState === "visible") tick(false); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [enabled, tick]);
}
